var _excluded = ["type", "id", "label", "hideLabel", "hasError", "helpText", "fieldMessage", "icon", "customIcon", "size", "optionalText", "className", "inputClassName"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import { string, oneOf, bool, element } from 'prop-types';
import classNames from 'classnames';
import uuid from 'uuid';
import Label from '../Label/Label';
import FormGroup from '../FormGroup/FormGroup';
import HelpText from '../HelpText/HelpText';
import FieldMessage from '../FieldMessage/FieldMessage';
import Input from '../Input/Input';
var propTypes = {
  /** Label text */
  label: string.isRequired,

  /** The component will generate an id for you.<br>
   * If you need to create your own, pass it in as a prop */
  id: string,

  /** Visually hide the label */
  hideLabel: bool,

  /** Visual presentation of an hasError in the input */
  hasError: bool,

  /** Help text below the input form field */
  helpText: string,

  /** Usually used for validation */
  fieldMessage: string,

  /** Adds an icon of your choice to the __right__ side of the input.
   * See [Icons](#/Visual%20Style/Icons) for a complete overview of icon names.
   * Only applicable when type is `text`, `search`, `email`, `url` or `tel` */
  icon: element,

  /** The path to your custom svg icon (e.g. 'img/my-custom.svg') */
  customIcon: string,

  /** The size */
  size: oneOf(['small', 'medium', 'large']),

  /** Text describing the input as optional */
  optionalText: string,

  /** Choose between these different types of inputs */
  type: oneOf(['text', 'search', 'email', 'url', 'tel', 'number', 'date', 'month', 'week', 'time', 'datetime', 'datetime-local', 'color', 'file', 'password']),

  /** Add your own custom class names to the component */
  className: string,

  /** Add your own custom class names directly to the `input` inside the group*/
  inputClassName: string
};
var defaultProps = {
  type: 'text',
  size: 'medium'
};
/** The input group is a packaged component that includes an Input, a Label, a HelpText and a FieldMessage. Everything is wrapped in a FormGroup.
 * @deprecated Use [`<FormGroup>`](/#/Components/Forms/FormGroup) instead.
 * @visibleName Input Group
 * */

var InputGroup = function InputGroup(_ref) {
  var type = _ref.type,
      id = _ref.id,
      label = _ref.label,
      hideLabel = _ref.hideLabel,
      hasError = _ref.hasError,
      helpText = _ref.helpText,
      fieldMessage = _ref.fieldMessage,
      icon = _ref.icon,
      customIcon = _ref.customIcon,
      size = _ref.size,
      optionalText = _ref.optionalText,
      className = _ref.className,
      inputClassName = _ref.inputClassName,
      rest = _objectWithoutProperties(_ref, _excluded);

  var helpTextId = "rds-form-text-".concat(uuid.v4());
  var fieldMessageId = "rds-fieldmessage-".concat(uuid.v4());
  var inputId = id || "rds-input-".concat(uuid.v4());
  var componentClassNames = classNames({
    'rds-form-group--danger': hasError
  }, className);
  /* Create the aria-describedby text based on what elements are present*/

  var ariaDescribedBy;

  if (fieldMessage && helpText) {
    ariaDescribedBy = "".concat(helpTextId, " ").concat(fieldMessageId);
  } else if (helpText) {
    ariaDescribedBy = helpTextId;
  } else if (fieldMessage) {
    ariaDescribedBy = fieldMessageId;
  } else {
    ariaDescribedBy = null;
  }
  /* Show a notification if someone tries to use input type range */


  if (type === 'range') return /*#__PURE__*/React.createElement("div", {
    className: 'rds-danger'
  }, "Please use Slider component!");
  console.error('InputGroup is deprecated. Use FormGroup instead');
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: componentClassNames
  }, /*#__PURE__*/React.createElement(Label, {
    labelText: label,
    hideLabel: hideLabel,
    htmlFor: inputId,
    optionalText: optionalText
  }), /*#__PURE__*/React.createElement(Input, _extends({
    size: size,
    hasError: hasError,
    type: type,
    id: inputId,
    icon: icon,
    customIcon: customIcon,
    className: inputClassName
  }, ariaDescribedBy && {
    'aria-describedby': ariaDescribedBy
  }, rest)), fieldMessage && /*#__PURE__*/React.createElement(FieldMessage, {
    text: fieldMessage,
    skin: hasError ? 'danger' : null,
    id: fieldMessageId
  }), helpText && /*#__PURE__*/React.createElement(HelpText, {
    text: helpText,
    id: helpTextId
  }));
};

InputGroup.propTypes = propTypes;
InputGroup.defaultProps = defaultProps;
export default InputGroup;